import { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, X, Camera, Filter } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PhotoAuditPane = ({ masterPassword }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [empFilter, setEmpFilter] = useState("");
  const [zoomed, setZoomed] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { master_code: masterPassword, date };
      if (empFilter.trim()) params.employee_code = empFilter.trim();
      const res = await axios.get(`${API}/timeclock/punch-photos`, { params });
      setPhotos(res.data || []);
    } catch (err) {
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  const fmtTime = (iso) => {
    try {
      return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/Chicago" });
    } catch { return ""; }
  };

  const typeColor = (t) =>
    t === "in" ? "bg-green-100 text-green-800" :
    t === "out" ? "bg-red-100 text-red-800" :
    "bg-amber-100 text-amber-800";

  return (
    <div className="bg-white rounded-lg p-3" data-testid="photo-audit-pane">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Filter className="w-4 h-4 text-[#666]" />
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="border border-stone-300 rounded px-2 py-1 text-sm"
          data-testid="photo-audit-date"
        />
        <input
          type="text"
          placeholder="Filter by employee code"
          value={empFilter}
          onChange={(e) => setEmpFilter(e.target.value)}
          onBlur={load}
          onKeyDown={(e) => e.key === "Enter" && load()}
          className="border border-stone-300 rounded px-2 py-1 text-sm w-44"
          data-testid="photo-audit-emp-filter"
        />
        <button onClick={load} className="bg-[#A61B1B] text-white text-sm px-3 py-1 rounded">
          Refresh
        </button>
        <span className="text-xs text-stone-500 ml-auto">
          {photos.length} photo{photos.length !== 1 ? "s" : ""} · auto-deleted after 90 days
        </span>
      </div>

      {loading ? (
        <div className="text-center text-stone-500 py-12">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading…
        </div>
      ) : photos.length === 0 ? (
        <div className="text-center text-stone-400 py-12">
          <Camera className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No punch photos for this date.</p>
          <p className="text-xs mt-1">Photos are captured when employees punch in/out via the iPad Kiosk (/kiosk).</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="photo-audit-grid">
          {photos.map((p) => (
            <button
              key={p.punch_id}
              onClick={() => setZoomed(p)}
              className="group relative bg-stone-100 rounded-lg overflow-hidden aspect-square hover:ring-2 hover:ring-[#A61B1B] transition-all"
              data-testid={`photo-thumb-${p.punch_id}`}
            >
              <img
                src={`${API}/timeclock/punch-photo/${p.punch_id}`}
                alt={p.employee_name}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-1.5 text-left">
                <div className="text-white text-[11px] font-semibold leading-tight truncate">{p.employee_name}</div>
                <div className="flex items-center justify-between gap-1">
                  <span className={`text-[9px] uppercase font-bold px-1 rounded ${typeColor(p.punch_type)}`}>
                    {p.punch_type.replace("_", " ")}
                  </span>
                  <span className="text-white/80 text-[10px]">{fmtTime(p.timestamp)}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Zoom modal */}
      {zoomed && (
        <div
          className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4"
          onClick={() => setZoomed(null)}
          data-testid="photo-zoom-modal"
        >
          <div className="bg-white rounded-xl max-w-lg w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-3 border-b flex items-center justify-between">
              <div>
                <div className="font-bold">{zoomed.employee_name}</div>
                <div className="text-xs text-stone-500">
                  #{zoomed.employee_code} ·{" "}
                  <span className={`uppercase font-bold px-1 rounded ${typeColor(zoomed.punch_type)}`}>{zoomed.punch_type.replace("_", " ")}</span>{" "}
                  · {zoomed.date} {fmtTime(zoomed.timestamp)}
                </div>
              </div>
              <button onClick={() => setZoomed(null)} className="text-stone-500 hover:text-stone-800">
                <X className="w-5 h-5" />
              </button>
            </div>
            <img
              src={`${API}/timeclock/punch-photo/${zoomed.punch_id}`}
              alt={zoomed.employee_name}
              className="w-full max-h-[70vh] object-contain bg-stone-900"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoAuditPane;

export const PunchPhotoThumb = ({ punchId }) => {
  const [failed, setFailed] = useState(false);
  const [zoom, setZoom] = useState(false);
  if (failed) {
    return (
      <div className="w-10 h-10 rounded bg-stone-100 flex items-center justify-center" title="No photo">
        <Camera className="w-4 h-4 text-stone-300" />
      </div>
    );
  }
  return (
    <>
      <button onClick={() => setZoom(true)} className="w-10 h-10 rounded overflow-hidden bg-stone-100 ring-1 ring-stone-200 hover:ring-[#A61B1B]" data-testid={`punch-thumb-${punchId}`}>
        <img
          src={`${API}/timeclock/punch-photo/${punchId}`}
          alt="punch"
          className="w-full h-full object-cover"
          onError={() => setFailed(true)}
          loading="lazy"
        />
      </button>
      {zoom && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setZoom(false)}>
          <img
            src={`${API}/timeclock/punch-photo/${punchId}`}
            alt="punch"
            className="max-w-full max-h-[80vh] rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};
